import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Calendar,
  Plus,
  Trash2,
  Loader
} from 'lucide-react';
import toast from 'react-hot-toast';
import { eventService } from '../../api/taskService';
import RelationshipTypeSelector from './RelationshipTypeSelector';
import useSubscriptionGate from '../../hooks/useSubscriptionGate';

/**
 * EventCreationModal Component
 * Modal for creating new events with optional initial tasks
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Callback to close the modal
 * @param {Function} onEventCreated - Callback when event is successfully created
 * @param {string} familyId - Current family ID
 * @param {string} userId - Current user ID
 */
const EventCreationModal = ({ isOpen, onClose, onEventCreated, familyId, userId }) => {
  const [loading, setLoading] = useState(false);
  const { guardAction } = useSubscriptionGate();
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    assigned_families: [],
    relationship_type: 'nuclear_family' // Default to free tier type
  });

  const [initialTasks, setInitialTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  // Add a task to the initial tasks list
  const addInitialTask = () => {
    if (!newTaskTitle.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    setInitialTasks([
      ...initialTasks,
      {
        id: Date.now(),
        title: newTaskTitle,
        description: '',
        task_type: 'task'
      }
    ]);
    setNewTaskTitle('');
  };

  // Remove a task from the initial tasks list
  const removeInitialTask = (taskId) => {
    setInitialTasks(initialTasks.filter(t => t.id !== taskId));
  };

  // Handle event creation
  const handleCreateEvent = async () => {
    if (!eventData.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }

    if (!eventData.start_date) {
      toast.error('Please select a start date');
      return;
    }

    if (!eventData.end_date) {
      toast.error('Please select an end date');
      return;
    }

    // Validate end date is after start date
    if (new Date(eventData.end_date) < new Date(eventData.start_date)) {
      toast.error('End date must be after start date');
      return;
    }

    // Check subscription limits before creating event
    if (!guardAction('createEvent')) {
      return; // User will see upgrade prompt
    }

    // Check if relationship type is allowed
    if (!guardAction('useRelationshipType', { relationshipTypeId: eventData.relationship_type })) {
      return; // User will see upgrade prompt
    }

    setLoading(true);

    try {
      // Prepare event data for API
      const eventPayload = {
        title: eventData.title,
        description: eventData.description,
        start_date: eventData.start_date,
        end_date: eventData.end_date,
        task_type: 'event',
        family_id: familyId,
        created_by: userId,
        status: 'pending',
        visibility: 'family', // Default to family visibility
        relationship_type: eventData.relationship_type // Include relationship type
      };

      console.log('[EventCreationModal] Creating event:', eventPayload);

      // Create the event
      const createdEvent = await eventService.createEvent(eventPayload);

      console.log('[EventCreationModal] Event created:', createdEvent);

      // If there are initial tasks, create them under the event
      if (initialTasks.length > 0) {
        const taskPromises = initialTasks.map((task, index) =>
          eventService.addTaskToEvent(createdEvent.task_id || createdEvent.id, {
            title: task.title,
            description: task.description,
            task_type: 'task',
            event_id: createdEvent.task_id || createdEvent.id,
            family_id: familyId,
            created_by: userId,
            status: 'pending',
            task_order: index,
            visibility: 'family'
          })
        );

        await Promise.all(taskPromises);
        toast.success(`Event created with ${initialTasks.length} tasks!`);
      } else {
        toast.success('Event created successfully!');
      }

      // Reset form
      setEventData({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        assigned_families: [],
        relationship_type: 'nuclear_family'
      });
      setInitialTasks([]);

      // Callback to parent
      if (onEventCreated) {
        onEventCreated(createdEvent);
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error('[EventCreationModal] Failed to create event:', error);
      toast.error(error.message || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Calendar className="w-6 h-6 text-purple-500" />
              Create New Event
            </h2>
            <button
              onClick={onClose}
              className="btn-icon"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-5">
            {/* Relationship Type Selector */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
              <RelationshipTypeSelector
                value={eventData.relationship_type}
                onChange={(relationshipType) => setEventData({ ...eventData, relationship_type: relationshipType })}
                showLabel={true}
              />
            </div>

            {/* Event Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Event Title *
              </label>
              <input
                type="text"
                value={eventData.title}
                onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                placeholder="e.g., Birthday Party, Home Renovation, Wedding Planning"
                className="input"
              />
            </div>

            {/* Event Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={eventData.description}
                onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                placeholder="Enter event details..."
                className="input min-h-[80px]"
              />
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={eventData.start_date}
                  onChange={(e) => setEventData({ ...eventData, start_date: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date *
                </label>
                <input
                  type="date"
                  value={eventData.end_date}
                  onChange={(e) => setEventData({ ...eventData, end_date: e.target.value })}
                  className="input"
                  min={eventData.start_date}
                />
              </div>
            </div>

            {/* Initial Tasks Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Initial Tasks (Optional)
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Add tasks that need to be completed for this event
              </p>

              {/* Add Task Input */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addInitialTask();
                    }
                  }}
                  placeholder="Add a task..."
                  className="input flex-1"
                />
                <button
                  onClick={addInitialTask}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {/* Task List */}
              {initialTasks.length > 0 && (
                <div className="space-y-2">
                  {initialTasks.map((task) => (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {task.title}
                        </p>
                      </div>
                      <button
                        onClick={() => removeInitialTask(task.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="btn-secondary flex-1"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateEvent}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4" />
                    Create Event
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EventCreationModal;
